import type { ReactNode } from 'react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { ClipboardList, GraduationCap, StickyNote, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn';
import {
    hasLeadNotes, leadNotesModel, type LeadNoteField, type LeadNoteSection, type LeadNotesKind,
} from '../../lib/leadNotes';
import type { Lead } from '../../types/leads.types';

const KIND_META: Record<LeadNotesKind, { title: string; icon: LucideIcon }> = {
    modulo: { title: 'Dati del modulo', icon: ClipboardList },
    iscrizione: { title: 'Iscrizione al corso', icon: GraduationCap },
    note: { title: 'Note', icon: StickyNote },
};

function isSiNo(value: string): 'si' | 'no' | null {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'sì' || normalized === 'si') return 'si';
    if (normalized === 'no') return 'no';
    return null;
}

function FieldValue({ field }: { field: LeadNoteField }) {
    const flag = isSiNo(field.value);
    if (flag) {
        return (
            <span className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold',
                flag === 'si' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500',
            )}>
                <span className={cn('h-1.5 w-1.5 rounded-full', flag === 'si' ? 'bg-emerald-500' : 'bg-gray-400')} />
                {field.value}
            </span>
        );
    }
    // pre-line: il messaggio scritto nel modulo tiene i suoi a capo
    return <span className="whitespace-pre-line break-words text-gray-900">{field.value}</span>;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-start gap-3 sm:grid-cols-[8.5rem_minmax(0,1fr)] px-4 py-2.5 text-sm odd:bg-gray-50/70">
            <dt className="text-[13px] text-gray-500">{label}</dt>
            <dd className="min-w-0 font-medium text-gray-900">{children}</dd>
        </div>
    );
}

function Fields({ section }: { section: LeadNoteSection }) {
    return (
        <dl className="divide-y divide-gray-100">
            {section.fields.map((field, index) => (
                <Row key={`${section.title}-${field.label}-${index}`} label={field.label}>
                    <FieldValue field={field} />
                </Row>
            ))}
        </dl>
    );
}

/** Più sezioni (l'iscrizione ai corsi): riquadri affiancati sugli schermi larghi. */
function SectionGrid({ sections }: { sections: LeadNoteSection[] }) {
    return (
        <div className="grid gap-3 p-3 md:grid-cols-2">
            {sections.map((section) => (
                <div key={section.title} className="overflow-hidden rounded-lg border border-gray-100">
                    <div className="flex items-center gap-2 border-b border-gray-100 bg-white px-4 py-2">
                        <span className="h-3.5 w-1 rounded-full bg-primary-500" />
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-primary-700">{section.title}</span>
                    </div>
                    <Fields section={section} />
                </div>
            ))}
        </div>
    );
}

/**
 * Quello che la persona ha scritto nel modulo (sito, Meta, Google, iscrizione
 * corsi) e le note del lead. Sta sotto le Attività; se non c'è niente da
 * mostrare non compare.
 */
export function LeadNotesCard({ lead }: { lead: Lead }) {
    const model = leadNotesModel({ notes: lead.notes, raw: lead.raw });
    if (!model || !hasLeadNotes(model)) return null;

    const { title, icon: TitleIcon } = KIND_META[model.kind];
    const origin = model.kind === 'modulo' ? lead.form_name : model.headline;
    const received = format(new Date(lead.created_at), "d MMM yyyy 'alle' HH:mm", { locale: it });
    const subtitle = model.kind === 'note' ? null : [origin, `ricevuto il ${received}`].filter(Boolean).join(' · ');

    return (
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center gap-2.5 border-b border-primary-100 bg-primary-50 px-4 py-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-600 text-white">
                    <TitleIcon className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                    {/* niente h2: su telefono il foglio di stile globale lo ingrandisce, gli altri riquadri usano testo semplice */}
                    <div className="text-sm font-semibold text-gray-900">{title}</div>
                    {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
                </div>
            </div>

            {model.sections.length > 1 && <SectionGrid sections={model.sections} />}
            {model.sections.length === 1 && <Fields section={model.sections[0]} />}

            {model.leftover && (
                <div className="border-t border-gray-100 px-4 py-3">
                    {model.kind !== 'note' && (
                        <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            <StickyNote className="h-3.5 w-3.5" /> Note
                        </div>
                    )}
                    <p className="whitespace-pre-line text-sm leading-relaxed text-gray-800">{model.leftover}</p>
                </div>
            )}
        </section>
    );
}

export default LeadNotesCard;
